import React, { useCallback, useEffect, useMemo, useState } from 'react';
import shallow from 'zustand/shallow';
import { useStore } from '../../store/store';
import {
  selectionOutline,
} from '../../utils/styles';
// Common
import './Utils.css';
import { getPrimaryDark, ItemTypes, MASKS_TYPE, viewerTypes } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import RichEditor from '../RichEditor';
import Image from '../Image/Image';
import OrgChart from '../OrgChart/OrgChart';
import Line from '../Line';
import Table from '../Table';
import Title from '../Title/Title';
import Shape from '../Shape';
import Summary from '../Summary';
import LineBreak from './LineBreak';
import { isAnUrl } from '../../utils/browser';
import CheckboxsVariable from './CheckboxsVariable';
import SelectorVariable from './SelectorVariable';
import { isATitleBox, isAWritableBox, isAImgBox, isATableBox, getGroupBoxesDimensions } from '../../utils/boxes';
import { maxContainerHeight } from '../../utils/containers';
import SpaceBox from './SpaceBox';
import { useHandleBoxSelect } from '../../hooks';
import GroupBoxes from './GroupBoxes';
import AccordionPanelBox from '../AccordionPanelBox';
import InfoCardBox from '../InfoCardBox';
import { getBoxById, getMaskById, getContainerByColumnId } from '../../store/lookups';
import { LockedBoxOverlay } from '../../collab/LockedBoxOverlay';

const getStyles = (drawMode, box, header, footer) => {
  if (box.hideField) {
    return {
      display: 'none'
    }
  }
  if (drawMode || isATableBox(box.type) || box.type === ItemTypes.SUMMARY)
    return {
    }
  return {
    maxHeight: maxContainerHeight({ header, footer }),
    overflow: isATitleBox(box.type)
      ? 'visible'
      : isAWritableBox(box.type)
        ? 'hidden'
        : 'visible'
  }
}

export const selectComponent = (box, drawMode, isSelected) => {
  switch (box?.type) {
    case ItemTypes.TEXT:
    case ItemTypes.TEXT_VARIABLE:
      return <RichEditor box={box} drawMode={drawMode} isSelected={isSelected} />;
    case ItemTypes.TITLE:
    case ItemTypes.TITLE_2:
    case ItemTypes.TITLE_3:
    case ItemTypes.TITLE_4:
      return <Title box={box} drawMode={drawMode} />;
    case ItemTypes.IMG:
    case ItemTypes.IMG_VARIABLE:
      return <Image box={box} />;
    case ItemTypes.ORG_CHART:
      return <OrgChart box={box} />;
    case ItemTypes.SHAPE:
      return <Shape box={box} />;
    case ItemTypes.LINE:
      return <Line box={box} />;
    case ItemTypes.TABLE:
    case ItemTypes.TABLE_VARIABLE:
      return <Table box={box} drawMode={drawMode} />;
    case ItemTypes.SUMMARY:
      return <Summary box={box} />;
    case ItemTypes.LINE_BREAK:
      return <LineBreak box={box} />;
    case ItemTypes.CHECKBOXS_VARIABLE:
    case ItemTypes.QUESTION:
      return <CheckboxsVariable box={box} />;
    case ItemTypes.SELECTOR_VARIABLE:
      return <SelectorVariable box={box} />;
    case ItemTypes.SPACE:
      return <SpaceBox box={box} />;
    case ItemTypes.GROUP_BOXES:
      return <GroupBoxes box={box} isSelected={isSelected} />;
    case ItemTypes.ACCORDION_PANEL:
      return <AccordionPanelBox box={box} />;
    case ItemTypes.INFO_CARD:
      return <InfoCardBox box={box} />;
    default:
      return null;
  }
};

const selector =
  (id) =>
    ({
      groupSelection,
      slider,
      boxes,
      updateBox,
      masks,
      containers,
      currentProposal,
      fromPdf,
      zoomLevel,
      landscape,
      boxVariableSelectedId,
      boxLocks,
      user,
      currentDocument,
      selectedBoxId,
    }) => {
      const box = getBoxById(boxes, masks, id);
      const accentColor = getPrimaryDark(currentDocument?.type === 'template');
      // Membre du groupe sélectionné : aligner ou répartir les box d'un groupe
      // se calcule sur leur géométrie mesurée, il faut donc la tenir à jour même
      // quand c'est le cadre du groupe, et non la box, qui est sélectionné.
      const selectedBox = selectedBoxId
        ? getBoxById(boxes, masks, selectedBoxId)
        : undefined;
      const isInSelectedGroupBox =
        selectedBox?.type === ItemTypes.GROUP_BOXES &&
        !!selectedBox.boxIds?.includes(id);
      // Verrou collab détenu par un co-éditeur (jamais le sien)
      const remoteLock =
        boxLocks[id] && boxLocks[id].id !== user?.id ? boxLocks[id] : null;

      if (!box) {
        return {
          isInSelectedGroup: false,
          isMultiSelection: false,
          isInSelectedGroupBox: false,
          isSlided: false,
          box: undefined,
          updateBox,
          container: undefined,
          currentProposal,
          fromPdf,
          maskTypeEdiatble: true,
          zoomLevel,
          landscape,
          boxesInGroup: undefined,
          boxVariableSelectedId,
          header: undefined,
          footer: undefined,
          remoteLock: null,
          accentColor
        };
      }

      const header = getMaskById(masks, MASKS_TYPE.HEADER.id);
      const footer = getMaskById(masks, MASKS_TYPE.FOOTER.id);
      const container = getContainerByColumnId(containers, box.columnId)
      const maskType = getMaskById(masks, MASKS_TYPE.HEADER.id)?.editableMask;
      const maskTypeEdiatble = typeof maskType !== 'boolean' ? true : maskType;

      let boxesInGroup;
      if (box.type === ItemTypes.GROUP_BOXES && box.boxIds) {
        boxesInGroup = [];
        for (const b of boxes) {
          if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
        }
        for (const m of masks) {
          for (const b of m.boxes) {
            if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
          }
        }
      }

      return {
        isInSelectedGroup: groupSelection.includes(id),
        isMultiSelection: groupSelection.length > 0,
        isInSelectedGroupBox,
        isSlided: slider.boxes.includes(id),
        box,
        updateBox,
        container,
        currentProposal,
        fromPdf,
        maskTypeEdiatble,
        zoomLevel,
        landscape,
        boxesInGroup,
        boxVariableSelectedId,
        header,
        footer,
        remoteLock,
        accentColor
      }
    };

const Box = React.memo(({ id, width, height, resizeStartWidth, resizeStartHeight, drawMode, fromViewer, isSelected }) => {
  const {
    isInSelectedGroup,
    isMultiSelection,
    isInSelectedGroupBox,
    isSlided,
    box,
    updateBox,
    container,
    currentProposal,
    fromPdf,
    maskTypeEdiatble,
    zoomLevel,
    landscape,
    boxesInGroup,
    boxVariableSelectedId,
    header,
    footer,
    remoteLock,
    accentColor
  } = useStore(selector(id), shallow);

  const boxRef = React.useRef(null);

  const [isHovered, setIsHovered] = useState(false);
  const measureBox = (suffix = 'from_measuring_effect') => {
    setTimeout(() => {
      if (boxRef.current) {
        const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
        const { top, left, width, height } = boxRef.current?.getBoundingClientRect();
        const {
          top: parentTop,
          left: parentLeft,
          width: parentWidth,
          height: parentHeight
        } = boxRef.current.parentElement.parentElement.getBoundingClientRect();

        // Coordonnées client (persistées) en tenant compte du zoom
        const boxLeft = (left / zoomFactor) - parentLeft;
        const boxTop = (top / zoomFactor) - parentTop;

        updateBox(
          box.id,
          (box) => {
            box.clientTop = boxTop;
            box.clientLeft = boxLeft;
            box.clientWidth = width / zoomFactor;
            box.clientHeight = height / zoomFactor;
          },
          { actionNameSuffix: suffix, ephemeral: true }
        );
      }
    }, 100);
  }

  // }, [box, box.top, box.left, updateBox])

  useEffect(() => {
    const shouldMeasure = box.type === ItemTypes.GROUP_BOXES && box.shouldMeasure && !boxVariableSelectedId
    if (shouldMeasure) {
      const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
      const { width, height, left, top } = getGroupBoxesDimensions(
        boxesInGroup,
        zoomFactor,
        box.rotation
      );
      if (box.left !== left || box.top !== top || box.height !== height || box.width !== width) {
        updateBox(
          box.id,
          (box) => {
            box.left = left
            box.top = top
            box.height = height
            box.width = width
            delete box.shouldMeasure
          },
          { ephemeral: true }
        );
        if (box.top !== top)
          boxesInGroup.forEach((b) => {
            updateBox(
              b.id,
              (box) => {
                box.groupTop = top
              },
              {
                shouldNotMeasureGroupBox: true,
                ephemeral: true
              }
            );
          })
      } else {
        updateBox(
          box.id,
          (box) => {
            delete box.shouldMeasure
          },
          { ephemeral: true }
        );
      }
    }
  }, [box.height, box.id, box.left, box.rotation, box.shouldMeasure, box.top, box.type, box.width, boxesInGroup, zoomLevel, landscape, updateBox, boxVariableSelectedId]);

  useEffect(() => {
    const shouldMeasure =
      isSelected || isInSelectedGroup || isInSelectedGroupBox || isSlided;
    if (shouldMeasure) {
      setTimeout(() => {
        measureBox();
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isInSelectedGroup, isInSelectedGroupBox, isSlided, box.rotation]);

  useEffect(() => {
    if (box?.alignToken) {
      setTimeout(() => {
        measureBox('from_align_token_effect');
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box?.alignToken]);

  const handleBoxSelect = useHandleBoxSelect({ fromViewer, box, isSelected, drawMode })

  const editableContainerHoverRule = useCallback(() => {
    if (fromViewer === viewerTypes.PAGENUMBER || fromViewer === viewerTypes.DMZ || fromViewer === viewerTypes.PREVIEW) return false
    if (currentProposal.id === null) {
      return true
    } else {
      if ((box.maskId === "header" || box.maskId === "footer") && !maskTypeEdiatble && box.type !== "img") return false
      if (container?.editable || !container?.hasOwnProperty("editable")) {
        return true
      } else {
        if (box.type === "img") {
          return true
        } else {
          return false
        }
      }
    }
  }, [container, currentProposal, box]);

  const boxWithOverrides = useMemo(() => ({
    ...box,
    fromViewer,
    width: width || box.width,
    height: height || box.height,
    // Transitoires (cf. Resizer.jsx) : présents seulement le temps d'un
    // glissement de la poignée générique, undefined sinon — jamais persistés
    // ni lus ailleurs qu'en aperçu de mise à l'échelle d'une forme composée
    // (cf. Shape/shapeUtils.getCompoundDesignScale).
    resizeStartWidth,
    resizeStartHeight,
  }), [box, fromViewer, width, height, resizeStartWidth, resizeStartHeight]);

  if (!box) { return null }

  return (
    <div
      ref={boxRef}
      id={`box-${box?.id ? box.id : 'new'}`}
      className={isSlided ? 'box-sliding' : ''}
      onMouseEnter={() => setIsHovered(editableContainerHoverRule())}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        handleBoxSelect(event)
      }}
      style={{
        ...selectionOutline({
          remoteLock,
          isSelected,
          isInSelectedGroup,
          isMultiSelection,
          isHovered,
          accentColor
        }),
        // position:relative seulement sous verrou (ancre l'étiquette, layout inchangé sinon)
        ...(remoteLock ? { position: 'relative' } : {}),
        zIndex: boxesInGroup ? (Math.max(...boxesInGroup.map((b) => ~~b.zIndex)) + ~~box?.zIndex) : box?.zIndex,
        ...((fromPdf || fromViewer === viewerTypes.PREVIEW) && isAImgBox(box?.type) &&
          !isAnUrl(box?.content?.src) ? { visibility: 'hidden' } : {}),
        ...getStyles(drawMode, box, header, footer)
      }}
    >
      {remoteLock && <LockedBoxOverlay lock={remoteLock} />}
      {selectComponent(boxWithOverrides, drawMode, isSelected)}
    </div>
  );
});

export default Box;
